/**
 * Luganda catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. Luganda at a mathematician's register. **Warrant → «Obukakafu»**
 * (proof/warrant — flagged). Scientific loanwords are used where Luganda standardly borrows
 * (logic → Lojiki, modal → Modali, heuristic, scalar, geometry). W/M grades stay Latin
 * (`::numbering-system/latn`). CLDR cardinal categories are one / other; the noun uses the
 * (li/ri)-/ga- class (ekkomo → amakomo). `frameActive` declares one/other plus an explicit
 * `=0`. Several abstract terms are flagged "expert-review suggested." Per INV-STABLE-ID a wrong
 * translation is a presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const LG_CATALOG = {
    locale: 'lg',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Okunoonyereza',
        [P2.sectionClaims]: 'Ebigambibwa',
        [P2.sectionIntegrity]: 'Obwesigwa',
        [P2.axisWarrantLabel]: 'Obukakafu',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: "Eky'empirika",
        [P2.axisEmpiricalValue]: "{kind, select, defn {Ennyinnyonnyola} interp {Okutaputa} testable {Kisoboka okugezesebwa} tested {Kigezeseddwa} refuted {Kiwakanyiziddwa} program {Pulogulaamu y'okunoonyereza} philos {Eky'efirosofi} other {Tekimanyiddwa}}",
        [P2.axisModalLabel]: 'Modali',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: "Omutendera gw'amaanyi {w, number, ::numbering-system/latn}",
        [P2.stateExplorable]: 'Kisoboka okunoonyezebwa',
        [P2.stateRefuted]: 'Kiwakanyiziddwa',
        [P2.stateOutside]: "Ebweru w'ekifo",
        [P2.verdictConflict]: 'Kikontana ne {constraints}',
        [P2.verdictOutside]: 'Kyetaaga {missing}',
        [P2.lossRegisterPlain]: 'Kyangiriddwa mu lulimi olwa bulijjo — obutuufu obumu bulekeddwawo.',
        [P2.lossRegisterKid]: 'Kyangiriddwa ku basomi abato — ebikwata bingi birekeddwawo.',
        [P2.lossProfileDetail]: "Profayiro enzijuvu eya Obukakafu / Eky'empirika / Modali efunziddwa wano.",
        [P2.lossConflictDetail]: 'Ebikwata ku nkaayana bifunziddwa mu ndabika eno.',
        [P2.laneVerified]: 'Kikakasiddwa',
        [P2.laneUnverified]: 'Tekikakasiddwa',
        [P2.laneConflict]: 'Enkaayana ekakasiddwa',
        [P2.constraintLogic]: 'Lojiki',
        [P2.constraintLightBends]: "Ekitangaala kikyama mu kifo ky'amaanyi g'okusika",
        [P2.constraintParallelPostulate]: "Postuleeti y'ennyiriri ezenkanankana",
        [P2.claimScalarGravity]: "Endowooza ya sukalaala ey'amaanyi g'okusika",
        [P2.claimNonEuclidean]: 'Jiyomesi etali ya Euclid',
        [P2.claimSubstrateSr]: "Okutaputa kwa substrate okw'enkolagana ey'enjawulo",
        [P2.edgeExtObservation]: "Okwekenneenya okw'ebweru",
        [P2.edgeResourceSample]: "Eby'obugagga eby'okubala ebigabanibwa",
        [P2.edgeHeuristicSample]: 'Obulagirizi bwa heuristic',
        [P2.edgeSharedSample]: 'Omusingi ogugabanibwa',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Amakomo',
        [P2.sectionEdges]: 'Enkugiro',
        [P2.sectionFrame]: 'Foleemu',
        [P2.sectionExposure]: 'Okwolekebwa',
        [P2.statusAdoptedFormal]: 'Kikkiriziddwa (mu ngeri entongole)',
        [P2.statusAdoptedInterpretive]: 'Kikkiriziddwa (mu kutaputa)',
        [P2.statusSuspended]: 'Kiyimiriziddwa',
        [P2.edgeEffectConstraint]: 'Kikola ekkomo (kisobola okuwakanya)',
        [P2.edgeEffectResource]: "Eby'obugagga — tewali kye bikola ku nsala",
        [P2.edgeEffectHeuristic]: 'Heuristic — tewali kye bikola ku nsala',
        [P2.edgeEffectShared]: 'Omusingi ogugabanibwa — tewali kye bikola ku nsala',
        [P2.frameActive]: '{count, plural, =0 {Tewali makomo gakola} one {# kkomo erikola} other {# makomo agakola}}',
        [P2.exposureNone]: 'Tewali kwesigama ku kutaputa',
        [P2.exposureDep]: 'Kyesigama ku {id}',
        [P2.laneTrackMachine]: 'Ekyuma',
        [P2.laneTrackExpert]: 'Omukugu',
        [P2.laneTrackCommunity]: 'Ekitundu',
        [P2.expertAudited]: 'Kikebereddwa',
        [P2.expertInProgress]: 'Kigenda mu maaso',
        [P2.expertNotReviewed]: 'Tekiddiŋŋanyiziddwa',
        [P2.communityFlags]: 'Waliwo obubonero',
        [P2.communityNone]: 'Tewali bubonero',
        [P2.controlChallenge]: 'Wakanya',
        [P2.controlActivate]: 'Kola',
        [P2.controlMakeInterpretive]: "Kifuule eky'okutaputa",
        [P2.controlImportEdge]: 'Yingiza enkugiro',
        [P2.controlPromote]: 'Yimusa',
        [P2.controlUndo]: 'Zzaako',
        [P2.controlRedo]: 'Ddamu okukola',
        [P2.controlRegister]: 'Wandiisa',
        [P2.promoteRejected]: 'Okuyimusa kugaaniddwa',
        [P2.promoteAccepted]: 'Okuyimusa kukkiriziddwa',
        [P2.promoteReasonRefutationTerminal]: 'Obukakafu tebusobola kuwonya kuwakanyizibwa.',
        [P2.promoteReasonKeystone]: 'Okuyimusa okukulu kwetaaga obuwagizi bwa empirika.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Ekika kya empirika tekinywezeddwa.',
        [P2.promoteReasonModalNotFixed]: 'Omutendera gwa modali tegunywezeddwa.',
        [P2.promoteReasonWarrantNotImproved]: "Omutendera gw'obukakafu tegulongooseddwa.",
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=lg.js.map